import * as THREE from 'three';

export function createPlayerController({
  camera,
  controls,
  world,
  onStep = null,
  onFallDamage = null,
  onFlightToggle = null
}) {
  const solidForCollisionAt = typeof world.isSolidForCollisionAt === 'function'
    ? (x, y, z) => world.isSolidForCollisionAt(x, y, z)
    : (x, y, z) => world.isSolidAt(x, y, z);

  const keys = {
    KeyW: false,
    KeyA: false,
    KeyS: false,
    KeyD: false,
    Space: false,
    KeyR: false,
    ShiftLeft: false,
    ShiftRight: false,
    ControlLeft: false,
    ControlRight: false,
  };

  const PLAYER_RADIUS = 0.32;
  const PLAYER_HEIGHT_STAND = 1.8;
  const PLAYER_HEIGHT_CROUCH = 1.5;
  const STEP_HEIGHT = 0.62;
  const GRAVITY = 22.0;
  const JUMP_SPEED = 7.4;
  const MAX_MOVE_SPEED = 9.2;
  const MAX_FLY_MOVE_SPEED = 12.5;
  const FLY_SPEED = 8.2;
  const FLY_SPRINT_SPEED = 11.0;
  const WATER_SWIM_SPEED = 2.2;
  const WATERCRAFT_BOAT_SPEED = 6.6;
  const WATERCRAFT_RAFT_SPEED = 5.7;
  const DOUBLE_TAP_SECONDS = 0.28;
  const GROUND_EPS = 0.08;
  const VOID_KILL_BUFFER = 24;
  const VOID_KILL_Y = (typeof world.getWorldMinY === 'function' ? world.getWorldMinY() : -22) - VOID_KILL_BUFFER;

  let jumpQueued = false;
  let sprinting = false;
  let crouching = false;
  let creativeMode = false;
  let flying = false;
  let lastSpaceTapAt = -1;
  let playerHeight = PLAYER_HEIGHT_STAND;
  let onGround = false;
  let inWater = false;
  let headInWater = false;
  let inLava = false;
  let headInLava = false;
  let inFire = false;
  let stepTravel = 0;
  let fallStartY = null;
  let watercraftMode = null;
  let watercraftDryTimer = 0;

  const velocity = new THREE.Vector3();
  const forwardVec = new THREE.Vector3();
  const rightVec = new THREE.Vector3();
  const wishDir = new THREE.Vector3();
  const stepOldPos = new THREE.Vector3();
  const standProbePos = new THREE.Vector3();
  const crouchProbePos = new THREE.Vector3();
  const resolveProbePos = new THREE.Vector3();
  const COLLISION_RESOLVE_OFFSETS = [
    [0, 0.18, 0],
    [0.12, 0, 0],
    [-0.12, 0, 0],
    [0, 0, 0.12],
    [0, 0, -0.12],
    [0.08, 0.08, 0],
    [-0.08, 0.08, 0],
    [0, 0.08, 0.08],
    [0, 0.08, -0.08]
  ];

  function setFlying(enabled) {
    const next = !!enabled;
    if (flying === next) return;
    flying = next;
    if (!flying) velocity.y = Math.min(velocity.y, 0);
    else velocity.y = 0;
    if (typeof onFlightToggle === 'function') onFlightToggle(flying);
  }

  function worldToBlock(v) {
    return Math.floor(v + 0.5);
  }

  function playerAABBAt(pos, height = playerHeight) {
    return {
      minX: pos.x - PLAYER_RADIUS,
      maxX: pos.x + PLAYER_RADIUS,
      minY: pos.y - height,
      maxY: pos.y,
      minZ: pos.z - PLAYER_RADIUS,
      maxZ: pos.z + PLAYER_RADIUS
    };
  }

  function collidesAt(pos, height = playerHeight) {
    const box = playerAABBAt(pos, height);
    const minX = worldToBlock(box.minX);
    const maxX = worldToBlock(box.maxX);
    const minY = worldToBlock(box.minY);
    const maxY = worldToBlock(box.maxY);
    const minZ = worldToBlock(box.minZ);
    const maxZ = worldToBlock(box.maxZ);

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          if (!solidForCollisionAt(x, y, z)) continue;
          return true;
        }
      }
    }
    return false;
  }

  function blockIntersectsPlayer(x, y, z, pos, height = playerHeight) {
    const bMinX = x - 0.5;
    const bMaxX = x + 0.5;
    const bMinY = y - 0.5;
    const bMaxY = y + 0.5;
    const bMinZ = z - 0.5;
    const bMaxZ = z + 0.5;
    const p = playerAABBAt(pos, height);

    return (
      p.minX < bMaxX && p.maxX > bMinX &&
      p.minY < bMaxY && p.maxY > bMinY &&
      p.minZ < bMaxZ && p.maxZ > bMinZ
    );
  }

  function pushPlayerOutOfBlocks(pos) {
    for (let iter = 0; iter < 20 && collidesAt(pos, playerHeight); iter++) {
      let moved = false;
      for (const [ox, oy, oz] of COLLISION_RESOLVE_OFFSETS) {
        resolveProbePos.set(pos.x + ox, pos.y + oy, pos.z + oz);
        if (!collidesAt(resolveProbePos, playerHeight)) {
          pos.copy(resolveProbePos);
          moved = true;
          break;
        }
      }
      if (!moved) pos.y += 0.16;
    }
  }

  function hasGroundSupport(pos, height = playerHeight) {
    const feetY = pos.y - height;
    const checkY = worldToBlock(feetY - GROUND_EPS);
    const r = PLAYER_RADIUS - 0.02;
    const samples = [
      [pos.x - r, pos.z - r],
      [pos.x + r, pos.z - r],
      [pos.x - r, pos.z + r],
      [pos.x + r, pos.z + r],
      [pos.x, pos.z]
    ];

    for (const [sx, sz] of samples) {
      const bx = worldToBlock(sx);
      const bz = worldToBlock(sz);
      if (!solidForCollisionAt(bx, checkY, bz)) continue;
      const topY = checkY + 0.5;
      if (feetY >= topY - 0.26 && feetY <= topY + 0.2) return true;
    }
    return false;
  }

  function getSurfaceBlockIdUnder(pos, height = playerHeight) {
    const checkY = worldToBlock(pos.y - height - GROUND_EPS);
    return world.getBlockIdAt(worldToBlock(pos.x), checkY, worldToBlock(pos.z));
  }

  function refreshGroundState(pos) {
    onGround = hasGroundSupport(pos, playerHeight) && velocity.y <= 1.7;
    return onGround;
  }

  function updateWaterState(pos, height = playerHeight) {
    const feetY = pos.y - height + 0.1;
    const chestY = pos.y - height * 0.45;
    const r = PLAYER_RADIUS - 0.03;
    const samples = [
      [pos.x, feetY, pos.z],
      [pos.x, chestY, pos.z],
      [pos.x - r, chestY, pos.z - r],
      [pos.x + r, chestY, pos.z - r],
      [pos.x - r, chestY, pos.z + r],
      [pos.x + r, chestY, pos.z + r]
    ];

    inWater = false;
    for (const [sx, sy, sz] of samples) {
      if (world.isWaterAt(worldToBlock(sx), worldToBlock(sy), worldToBlock(sz))) {
        inWater = true;
        break;
      }
    }
    return inWater;
  }

  function updateHeadWaterState(pos) {
    const headY = pos.y - 0.08;
    const r = PLAYER_RADIUS - 0.05;
    const samples = [
      [pos.x, headY, pos.z],
      [pos.x - r, headY, pos.z],
      [pos.x + r, headY, pos.z],
      [pos.x, headY, pos.z - r],
      [pos.x, headY, pos.z + r]
    ];

    headInWater = false;
    for (const [sx, sy, sz] of samples) {
      if (world.isWaterAt(worldToBlock(sx), worldToBlock(sy), worldToBlock(sz))) {
        headInWater = true;
        break;
      }
    }
    return headInWater;
  }

  function updateLavaState(pos, height = playerHeight) {
    const feetY = pos.y - height + 0.1;
    const chestY = pos.y - height * 0.45;
    const r = PLAYER_RADIUS - 0.03;
    const samples = [
      [pos.x, feetY, pos.z],
      [pos.x, chestY, pos.z],
      [pos.x - r, chestY, pos.z - r],
      [pos.x + r, chestY, pos.z - r],
      [pos.x - r, chestY, pos.z + r],
      [pos.x + r, chestY, pos.z + r]
    ];

    const canQueryLava = typeof world.isLavaAt === 'function';
    inLava = false;
    if (!canQueryLava) return inLava;
    for (const [sx, sy, sz] of samples) {
      if (world.isLavaAt(worldToBlock(sx), worldToBlock(sy), worldToBlock(sz))) {
        inLava = true;
        break;
      }
    }
    return inLava;
  }

  function updateHeadLavaState(pos) {
    const headY = pos.y - 0.08;
    const r = PLAYER_RADIUS - 0.05;
    const samples = [
      [pos.x, headY, pos.z],
      [pos.x - r, headY, pos.z],
      [pos.x + r, headY, pos.z],
      [pos.x, headY, pos.z - r],
      [pos.x, headY, pos.z + r]
    ];

    const canQueryLava = typeof world.isLavaAt === 'function';
    headInLava = false;
    if (!canQueryLava) return headInLava;
    for (const [sx, sy, sz] of samples) {
      if (world.isLavaAt(worldToBlock(sx), worldToBlock(sy), worldToBlock(sz))) {
        headInLava = true;
        break;
      }
    }
    return headInLava;
  }

  function updateFireState(pos, height = playerHeight) {
    const feetY = pos.y - height + 0.1;
    const chestY = pos.y - height * 0.45;
    const r = PLAYER_RADIUS - 0.03;
    const samples = [
      [pos.x, feetY, pos.z],
      [pos.x, chestY, pos.z],
      [pos.x - r, chestY, pos.z - r],
      [pos.x + r, chestY, pos.z - r],
      [pos.x - r, chestY, pos.z + r],
      [pos.x + r, chestY, pos.z + r]
    ];

    inFire = false;
    for (const [sx, sy, sz] of samples) {
      if (world.getBlockIdAt(worldToBlock(sx), worldToBlock(sy), worldToBlock(sz)) === 64) {
        inFire = true;
        break;
      }
    }
    return inFire;
  }

  function tryMoveAxis(pos, axis, amount) {
    if (amount === 0) return false;
    const steps = Math.max(1, Math.ceil(Math.abs(amount) / 0.03));
    const stepSize = amount / steps;
    let blocked = false;

    for (let i = 0; i < steps; i++) {
      pos[axis] += stepSize;
      if (collidesAt(pos, playerHeight)) {
        pos[axis] -= stepSize;
        blocked = true;
        break;
      }
    }

    if (blocked) velocity[axis] = 0;
    return blocked;
  }

  function tryStepUpAndMove(pos, dx, dz) {
    const oldY = pos.y;
    pos.y += STEP_HEIGHT + 0.02;
    if (collidesAt(pos, playerHeight)) {
      pos.y = oldY;
      return false;
    }

    const blockedX = tryMoveAxis(pos, 'x', dx);
    const blockedZ = tryMoveAxis(pos, 'z', dz);
    if (blockedX && blockedZ) {
      pos.y = oldY;
      return false;
    }

    for (let i = 0; i < 40; i++) {
      pos.y -= 0.03;
      if (collidesAt(pos, playerHeight)) {
        pos.y += 0.03;
        break;
      }
    }

    if (pos.y < oldY - 0.08) pos.y = oldY;
    onGround = true;
    return true;
  }

  function movePlayer(pos, dx, dz, dy) {
    if (dx !== 0 || dz !== 0) {
      stepOldPos.copy(pos);
      const blockedX = tryMoveAxis(pos, 'x', dx);
      const blockedZ = tryMoveAxis(pos, 'z', dz);

      if ((blockedX || blockedZ) && onGround && !crouching) {
        pos.copy(stepOldPos);
        const stepped = tryStepUpAndMove(pos, dx, dz);
        if (!stepped) {
          tryMoveAxis(pos, 'x', dx);
          tryMoveAxis(pos, 'z', dz);
        }
      }
    }

    if (dy !== 0) {
      const steps = Math.max(1, Math.ceil(Math.abs(dy) / 0.03));
      const stepSize = dy / steps;
      for (let i = 0; i < steps; i++) {
        pos.y += stepSize;
        if (collidesAt(pos, playerHeight)) {
          pos.y -= stepSize;
          if (stepSize < 0) onGround = true;
          velocity.y = 0;
          break;
        }
      }
    }
  }

  function update(delta) {
    const playerPos = controls.getObject().position;
    const prevX = playerPos.x;
    const prevZ = playerPos.z;
    const wasOnGround = onGround;

    if (!creativeMode && flying) setFlying(false);

    const descendHeld = keys.ShiftLeft || keys.ShiftRight;
    const wantCrouch = !flying && descendHeld;
    const sprintHeld = keys.ControlLeft || keys.ControlRight || keys.KeyR;
    sprinting = sprintHeld && !wantCrouch;

    if (flying) {
      if (crouching || playerHeight !== PLAYER_HEIGHT_STAND) {
        standProbePos.copy(playerPos);
        standProbePos.y += PLAYER_HEIGHT_STAND - playerHeight;
        if (!collidesAt(standProbePos, PLAYER_HEIGHT_STAND)) {
          playerPos.copy(standProbePos);
        }
        playerHeight = PLAYER_HEIGHT_STAND;
      }
      crouching = false;
    } else if (wantCrouch) {
      if (!crouching) {
        const oldHeight = playerHeight;
        playerHeight = PLAYER_HEIGHT_CROUCH;
        playerPos.y += playerHeight - oldHeight;
      } else {
        playerHeight = PLAYER_HEIGHT_CROUCH;
      }
      crouching = true;
    } else if (crouching) {
      standProbePos.copy(playerPos);
      standProbePos.y += PLAYER_HEIGHT_STAND - playerHeight;
      if (!collidesAt(standProbePos, PLAYER_HEIGHT_STAND)) {
        playerPos.copy(standProbePos);
        playerHeight = PLAYER_HEIGHT_STAND;
        crouching = false;
      } else {
        playerHeight = PLAYER_HEIGHT_CROUCH;
        crouching = true;
      }
    } else {
      playerHeight = PLAYER_HEIGHT_STAND;
      crouching = false;
    }

    refreshGroundState(playerPos);
    updateWaterState(playerPos, playerHeight);
    updateHeadWaterState(playerPos);
    updateLavaState(playerPos, playerHeight);
    updateHeadLavaState(playerPos);
    updateFireState(playerPos, playerHeight);
    if (watercraftMode) {
      if (inWater) {
        watercraftDryTimer = 0;
      } else {
        watercraftDryTimer += Math.max(0, delta);
        if (watercraftDryTimer >= 1.25) {
          watercraftMode = null;
          watercraftDryTimer = 0;
        }
      }
    }
    if (onGround && !inWater) fallStartY = playerPos.y;

    forwardVec.set(0, 0, -1).applyQuaternion(camera.quaternion);
    rightVec.set(1, 0, 0).applyQuaternion(camera.quaternion);
    forwardVec.y = 0;
    rightVec.y = 0;
    forwardVec.normalize();
    rightVec.normalize();

    let moveX = 0;
    let moveZ = 0;
    if (keys.KeyW) moveZ += 1;
    if (keys.KeyS) moveZ -= 1;
    if (keys.KeyD) moveX += 1;
    if (keys.KeyA) moveX -= 1;
    const moveLen = Math.hypot(moveX, moveZ) || 1;
    moveX /= moveLen;
    moveZ /= moveLen;

    const watercraftActive = inWater && !!watercraftMode;
    const waterMoveBase = watercraftMode === 'boat'
      ? WATERCRAFT_BOAT_SPEED
      : (watercraftMode === 'bamboo_raft' ? WATERCRAFT_RAFT_SPEED : WATER_SWIM_SPEED);
    const waterMoveSpeed = watercraftActive && sprinting ? waterMoveBase * 1.55 : waterMoveBase;
    const speed = flying
      ? (sprinting ? 9.4 : 6.8)
      : inWater
        ? (watercraftActive ? waterMoveSpeed : WATER_SWIM_SPEED)
        : (crouching ? 2.2 : sprinting ? 8.2 : 5.4);

    wishDir.set(0, 0, 0)
      .addScaledVector(rightVec, moveX)
      .addScaledVector(forwardVec, moveZ);
    if (wishDir.lengthSq() > 0) wishDir.normalize();

    const groundAccel = flying ? 16.0 : (inWater ? (watercraftActive ? 13.2 : 9.5) : (onGround ? 22 : 6.3));
    const friction = flying ? 8.4 : (inWater ? (watercraftActive ? 2.9 : 5.8) : (onGround ? 11 : 2.2));
    velocity.x += (wishDir.x * speed - velocity.x) * Math.min(1, groundAccel * delta);
    velocity.z += (wishDir.z * speed - velocity.z) * Math.min(1, groundAccel * delta);
    velocity.x -= velocity.x * Math.min(1, friction * delta);
    velocity.z -= velocity.z * Math.min(1, friction * delta);
    const maxHorizontalSpeed = flying ? MAX_FLY_MOVE_SPEED : MAX_MOVE_SPEED;
    velocity.x = THREE.MathUtils.clamp(velocity.x, -maxHorizontalSpeed, maxHorizontalSpeed);
    velocity.z = THREE.MathUtils.clamp(velocity.z, -maxHorizontalSpeed, maxHorizontalSpeed);

    const jumpHeld = keys.Space || jumpQueued;

    if (flying) {
      const flyInput = (jumpHeld ? 1 : 0) - (descendHeld ? 1 : 0);
      const flySpeed = sprinting ? FLY_SPRINT_SPEED : FLY_SPEED;
      velocity.y = flyInput * flySpeed;
      onGround = false;
      fallStartY = playerPos.y;
    } else if (inWater) {
      if (watercraftActive) {
        if (jumpHeld) velocity.y += 4.6 * delta;
        if (crouching) velocity.y -= 2.8 * delta;
        velocity.y += 2.4 * delta;
        velocity.y -= 1.4 * delta;
        velocity.y = THREE.MathUtils.clamp(velocity.y, -1.4, 2.6);
      } else {
        if (jumpHeld) velocity.y += 8.2 * delta;
        if (crouching) velocity.y -= 6.4 * delta;
        else velocity.y += 3.8 * delta;
        velocity.y -= 3.6 * delta;
        velocity.y = THREE.MathUtils.clamp(velocity.y, -2.9, 3.8);
      }
    } else {
      const canJumpNow = (onGround || hasGroundSupport(playerPos, playerHeight)) && velocity.y <= 0.6;
      if (jumpHeld && canJumpNow && !crouching) {
        velocity.y = JUMP_SPEED;
        onGround = false;
        if (!keys.Space) jumpQueued = false;
      }
      velocity.y -= GRAVITY * delta;
    }

    let dx = velocity.x * delta;
    let dz = velocity.z * delta;

    // Crouch edge safety: stop movement when there is no ground support.
    if (!inWater && crouching && onGround && (dx !== 0 || dz !== 0)) {
      crouchProbePos.copy(playerPos);
      crouchProbePos.x += dx;
      crouchProbePos.z += dz;
      if (!hasGroundSupport(crouchProbePos, playerHeight)) {
        dx = 0;
        dz = 0;
        velocity.x = 0;
        velocity.z = 0;
      }
    }

    const dy = velocity.y * delta;
    movePlayer(playerPos, dx, dz, dy);
    pushPlayerOutOfBlocks(playerPos);
    refreshGroundState(playerPos);
    updateWaterState(playerPos, playerHeight);
    updateHeadWaterState(playerPos);
    updateLavaState(playerPos, playerHeight);
    updateHeadLavaState(playerPos);
    updateFireState(playerPos, playerHeight);

    const movedXZ = Math.hypot(playerPos.x - prevX, playerPos.z - prevZ);
    if (!flying && (onGround || inWater) && movedXZ > 0.0005) {
      const stepInterval = inWater ? 1.15 : (crouching ? 0.92 : sprinting ? 0.72 : 0.86);
      stepTravel += movedXZ;
      while (stepTravel >= stepInterval) {
        const surfaceId = getSurfaceBlockIdUnder(playerPos, playerHeight);
        if (onStep) onStep(surfaceId, inWater, sprinting, crouching);
        stepTravel -= stepInterval;
      }
    } else if (flying || (!onGround && !inWater)) {
      stepTravel = 0;
    }

    if (flying) {
      fallStartY = playerPos.y;
    } else if (!onGround && !inWater) {
      if (fallStartY === null || playerPos.y > fallStartY) fallStartY = playerPos.y;
    } else if (onGround && !wasOnGround && !inWater && fallStartY !== null) {
      const fallDistance = fallStartY - playerPos.y;
      if (fallDistance > 3.15 && onFallDamage) {
        const damage = Math.max(1, Math.floor(fallDistance - 3));
        onFallDamage(damage, 'fall', fallDistance);
      }
      fallStartY = playerPos.y;
    } else if (onGround) {
      fallStartY = playerPos.y;
    }

    if (playerPos.y < VOID_KILL_Y) {
      velocity.set(0, 0, 0);
      if (onFallDamage) {
        onFallDamage(9999, 'void');
      } else {
        playerPos.set(0, world.getSurfaceHeight(0, 0) + playerHeight + 2.5, 0);
        onGround = false;
        fallStartY = null;
        stepTravel = 0;
      }
    }
  }

  function onKeyDown(e) {
    if (Object.prototype.hasOwnProperty.call(keys, e.code)) keys[e.code] = true;
    if (e.code === 'Space') {
      if (!e.repeat) {
        const now = performance.now() / 1000;
        if (creativeMode && lastSpaceTapAt > 0 && (now - lastSpaceTapAt) <= DOUBLE_TAP_SECONDS) {
          setFlying(!flying);
          lastSpaceTapAt = -1;
        } else {
          lastSpaceTapAt = now;
        }
      }
      jumpQueued = true;
    }
  }

  function onKeyUp(e) {
    if (Object.prototype.hasOwnProperty.call(keys, e.code)) keys[e.code] = false;
    if (e.code === 'Space') jumpQueued = false;
  }

  function resetInput() {
    for (const key of Object.keys(keys)) keys[key] = false;
    jumpQueued = false;
    sprinting = false;
    crouching = false;
    lastSpaceTapAt = -1;
    headInWater = false;
    inLava = false;
    headInLava = false;
    inFire = false;
    stepTravel = 0;
    fallStartY = null;
    velocity.set(0, 0, 0);
  }

  function setCreativeMode(enabled) {
    creativeMode = !!enabled;
    lastSpaceTapAt = -1;
    if (!creativeMode) setFlying(false);
  }

  function setWatercraftMode(mode = null) {
    const next = (mode === 'boat' || mode === 'bamboo_raft') ? mode : null;
    watercraftMode = next;
    watercraftDryTimer = 0;
  }

  function getWatercraftMode() {
    return watercraftMode;
  }

  function getHeight() {
    return playerHeight;
  }

  function getPosition() {
    return controls.getObject().position;
  }

  function getFeetY() {
    return controls.getObject().position.y - playerHeight;
  }

  function isCrouching() {
    return crouching;
  }

  function isInWater() {
    return inWater;
  }

  function isHeadInWater() {
    return headInWater;
  }

  function isInLava() {
    return inLava;
  }

  function isHeadInLava() {
    return headInLava;
  }

  return {
    update,
    onKeyDown,
    onKeyUp,
    resetInput,
    getHeight,
    getPosition,
    getFeetY,
    isCrouching,
    isInWater,
    isHeadInWater,
    isInLava,
    isHeadInLava,
    isInFire: () => inFire,
    setCreativeMode,
    setWatercraftMode,
    getWatercraftMode,
    isFlying() {
      return flying;
    },
    blockIntersectsPlayer
  };
}
